import { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import confetti from 'canvas-confetti';

let audioCtx = null;
function getAudioCtx() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtx;
}

function playTick() {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'triangle';
    osc.frequency.value = 1200 + Math.random() * 400;
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.035);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.035);
  } catch { /* audio blocked by browser policy - non-fatal */ }
}

function playWinSound() {
  try {
    const ctx = getAudioCtx();
    [523, 659, 784, 1047, 1318, 1568].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, ctx.currentTime + i * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.1 + 0.3);
      osc.start(ctx.currentTime + i * 0.1);
      osc.stop(ctx.currentTime + i * 0.1 + 0.3);
    });
  } catch { /* audio blocked by browser policy - non-fatal */ }
}

function playLoseSound() {
  try {
    const ctx = getAudioCtx();
    [350, 280, 200].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.07, ctx.currentTime + i * 0.22);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.22 + 0.35);
      osc.start(ctx.currentTime + i * 0.22);
      osc.stop(ctx.currentTime + i * 0.22 + 0.35);
    });
  } catch { /* audio blocked by browser policy - non-fatal */ }
}

function fireConfetti() {
  const end = Date.now() + 3500;
  const colors = ['#FFD700', '#7C3AED', '#F59E0B', '#10B981', '#EC4899', '#3B82F6'];
  (function frame() {
    confetti({ particleCount: 3, angle: 60, spread: 60, origin: { x: 0, y: 0.6 }, colors, gravity: 0.8 });
    confetti({ particleCount: 3, angle: 120, spread: 60, origin: { x: 1, y: 0.6 }, colors, gravity: 0.8 });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

function hexToRgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function lighten(hex, pct) {
  const [r, g, b] = hexToRgb(hex);
  const f = pct / 100;
  return `rgb(${Math.min(255, Math.round(r + (255 - r) * f))},${Math.min(255, Math.round(g + (255 - g) * f))},${Math.min(255, Math.round(b + (255 - b) * f))})`;
}
function darken(hex, pct) {
  const [r, g, b] = hexToRgb(hex);
  const f = 1 - pct / 100;
  return `rgb(${Math.round(r * f)},${Math.round(g * f)},${Math.round(b * f)})`;
}

const SpinWheel = forwardRef(function SpinWheel({ slots = [], size = 340, onSpinEnd, preview = false }, ref) {
  const canvasRef = useRef(null);
  const rotRef = useRef(0);
  const spinRef = useRef(false);
  const lastSegRef = useRef(-1);
  const animRef = useRef(null);
  const ledRef = useRef(0);

  const draw = useCallback((rotation = 0, ledPhase = 0) => {
    const canvas = canvasRef.current;
    if (!canvas || slots.length === 0) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = size + 'px';
    canvas.style.height = size + 'px';
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const outerR = cx - 6;
    const ringW = Math.max(12, outerR * 0.065);
    const r = outerR - ringW;
    const pegR = outerR - ringW * 0.5;
    const n = slots.length;
    const sliceAngle = (Math.PI * 2) / n;

    ctx.clearRect(0, 0, size, size);

    // === SHADOW under wheel ===
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 6;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.01)';
    ctx.fill();
    ctx.restore();

    // === OUTER METALLIC RING ===
    const ringGrad = ctx.createRadialGradient(cx, cy, r - 2, cx, cy, outerR + 1);
    ringGrad.addColorStop(0, '#8B6914');
    ringGrad.addColorStop(0.2, '#FFD700');
    ringGrad.addColorStop(0.45, '#FFFACD');
    ringGrad.addColorStop(0.55, '#FFD700');
    ringGrad.addColorStop(0.8, '#B8860B');
    ringGrad.addColorStop(1, '#6B4F1A');
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.arc(cx, cy, r, 0, Math.PI * 2, true);
    ctx.fillStyle = ringGrad;
    ctx.fill();

    // Outer edge highlight
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,250,205,0.3)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Inner ring edge
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(139,105,20,0.6)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // === LED PEGS ===
    const numPegs = n * 2;
    for (let i = 0; i < numPegs; i++) {
      const a = (i / numPegs) * Math.PI * 2 - Math.PI / 2;
      const px = cx + Math.cos(a) * pegR;
      const py = cy + Math.sin(a) * pegR;
      const lit = Math.floor(ledPhase + i) % 3 === 0;
      const ps = Math.max(2, ringW * 0.18);

      ctx.save();
      if (lit) {
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 6;
      }
      ctx.beginPath();
      ctx.arc(px, py, ps, 0, Math.PI * 2);
      ctx.fillStyle = lit ? '#FFFACD' : '#7A6520';
      ctx.fill();
      ctx.restore();
    }

    // === EQUAL-SIZE SEGMENTS ===
    for (let i = 0; i < n; i++) {
      const slot = slots[i];
      const startA = rotation - Math.PI / 2 + i * sliceAngle;
      const endA = startA + sliceAngle;
      const midA = startA + sliceAngle / 2;
      const color = slot.color || '#7C3AED';

      // Segment with gradient
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r - 1, startA, endA);
      ctx.closePath();

      const segGrad = ctx.createRadialGradient(cx, cy, r * 0.15, cx, cy, r);
      segGrad.addColorStop(0, lighten(color, 25));
      segGrad.addColorStop(0.5, color);
      segGrad.addColorStop(1, darken(color, 20));
      ctx.fillStyle = segGrad;
      ctx.fill();

      // Thin gold separator line
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(startA) * (r - 1), cy + Math.sin(startA) * (r - 1));
      ctx.strokeStyle = 'rgba(255,215,0,0.35)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // === LABEL (along the radius) ===
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(midA);
      const fs = Math.max(11, Math.min(15, r / 11));
      ctx.font = `700 ${fs}px 'Poppins', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      ctx.fillStyle = '#FFFFFF';
      const lbl = slot.label;
      // Split long labels into 2 lines
      if (lbl.length > 10) {
        const words = lbl.split(' ');
        if (words.length >= 2) {
          const mid = Math.ceil(words.length / 2);
          const line1 = words.slice(0, mid).join(' ');
          const line2 = words.slice(mid).join(' ');
          ctx.fillText(line1, r * 0.55, -fs * 0.55);
          ctx.fillText(line2, r * 0.55, fs * 0.55);
        } else {
          ctx.fillText(lbl, r * 0.55, 0);
        }
      } else {
        ctx.fillText(lbl, r * 0.55, 0);
      }
      ctx.restore();
    }

    // Inner shadow overlay
    const innerS = ctx.createRadialGradient(cx, cy, r * 0.85, cx, cy, r);
    innerS.addColorStop(0, 'transparent');
    innerS.addColorStop(1, 'rgba(0,0,0,0.18)');
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = innerS;
    ctx.fill();

    // === CENTER HUB ===
    const hubR = r * 0.16;

    // Outer gold ring of hub
    const hubG = ctx.createRadialGradient(cx, cy, hubR * 0.3, cx, cy, hubR);
    hubG.addColorStop(0, '#FFFACD');
    hubG.addColorStop(0.4, '#FFD700');
    hubG.addColorStop(1, '#8B6914');
    ctx.beginPath();
    ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
    ctx.fillStyle = hubG;
    ctx.fill();
    ctx.strokeStyle = 'rgba(139,105,20,0.5)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Inner dark circle
    const dG = ctx.createRadialGradient(cx, cy, 0, cx, cy, hubR * 0.72);
    dG.addColorStop(0, '#1E1040');
    dG.addColorStop(1, '#0A0A15');
    ctx.beginPath();
    ctx.arc(cx, cy, hubR * 0.72, 0, Math.PI * 2);
    ctx.fillStyle = dG;
    ctx.fill();
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 1;
    ctx.stroke();

    // SPIN text
    ctx.fillStyle = '#FFD700';
    ctx.font = `800 ${Math.max(8, hubR * 0.5)}px 'Outfit', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.fillText('SPIN', cx, cy);

    // === POINTER (top center) ===
    const ptrH = outerR * 0.16;
    const ptrW = outerR * 0.08;
    const ptrY = cy - outerR - 2;

    ctx.save();
    ctx.shadowColor = 'rgba(255,215,0,0.5)';
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.moveTo(cx - ptrW, ptrY);
    ctx.lineTo(cx + ptrW, ptrY);
    ctx.lineTo(cx, ptrY + ptrH);
    ctx.closePath();
    const pG = ctx.createLinearGradient(cx, ptrY, cx, ptrY + ptrH);
    pG.addColorStop(0, '#FFD700');
    pG.addColorStop(0.5, '#FFFACD');
    pG.addColorStop(1, '#F59E0B');
    ctx.fillStyle = pG;
    ctx.fill();
    ctx.strokeStyle = '#8B6914';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // Pointer tip dot
    ctx.beginPath();
    ctx.arc(cx, ptrY + ptrH - 1, 2, 0, Math.PI * 2);
    ctx.fillStyle = '#FFF';
    ctx.fill();
  }, [slots, size]);

  // LED idle animation
  useEffect(() => {
    if (!preview) { draw(rotRef.current, 0); return; }
    let f = 0;
    const loop = () => {
      f++;
      ledRef.current = f * 0.06;
      draw(rotRef.current, ledRef.current);
      animRef.current = requestAnimationFrame(loop);
    };
    loop();
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [slots, draw, preview]);

  useEffect(() => { if (!preview) draw(rotRef.current, 0); }, [slots, draw, preview]);

  useImperativeHandle(ref, () => ({
    spin(targetIndex) {
      if (spinRef.current || slots.length === 0) return;
      spinRef.current = true;
      try { getAudioCtx().resume(); } catch { /* audio blocked by browser policy - non-fatal */ }

      const n = slots.length;
      const sliceAngle = (Math.PI * 2) / n;
      // Random offset within the target segment (avoid edges: 15%-85% of segment)
      const segOffset = sliceAngle * (0.15 + Math.random() * 0.7);
      const targetAngle = targetIndex * sliceAngle + segOffset;
      // CRITICAL: fullSpins MUST be an integer multiple of 2π
      // so the offset calculation lands on the correct segment
      const fullSpins = Math.PI * 2 * (9 + Math.floor(Math.random() * 5));
      const finalAngle = -(fullSpins + targetAngle);

      const startAngle = rotRef.current;
      const delta = finalAngle - startAngle;
      const duration = 4500 + Math.random() * 1500;
      const start = performance.now();

      const ease = t => 1 - Math.pow(1 - t, 4);

      let lf = 0;
      const anim = (now) => {
        const elapsed = now - start;
        const p = Math.min(elapsed / duration, 1);
        const cur = startAngle + delta * ease(p);
        rotRef.current = cur;
        lf++;
        draw(cur, lf * 0.15);

        // Tick detection (equal segments)
        const normAngle = ((-cur % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const seg = Math.floor((normAngle / (Math.PI * 2)) * n) % n;
        if (seg !== lastSegRef.current) {
          if (p < 0.9) playTick();
          lastSegRef.current = seg;
        }

        if (p < 1) {
          requestAnimationFrame(anim);
        } else {
          spinRef.current = false;
          const isLosing = slots[targetIndex]?.is_losing;
          if (!isLosing) { playWinSound(); setTimeout(fireConfetti, 150); }
          else { playLoseSound(); }
          onSpinEnd?.(targetIndex);
        }
      };
      requestAnimationFrame(anim);
    },
    isSpinning: () => spinRef.current
  }));

  return (
    <div className="relative inline-flex items-center justify-center">
      <canvas
        ref={canvasRef}
        style={{ width: size, height: size }}
        className="max-w-full"
        data-testid="spin-wheel-canvas"
      />
    </div>
  );
});

export default SpinWheel;
